import React from 'react';
import Icon from '../../../components/AppIcon';

const PerformanceComparison = ({ currentScore, previousAttempts, personalBest }) => {
  const currentPercentage = Math.round((currentScore.score / currentScore.totalQuestions) * 100);
  
  const getImprovementTrend = () => {
    if (previousAttempts.length === 0) return null;
    
    const lastAttempt = previousAttempts[previousAttempts.length - 1];
    const lastPercentage = Math.round((lastAttempt.score / lastAttempt.totalQuestions) * 100);
    const improvement = currentPercentage - lastPercentage;
    
    return {
      value: improvement,
      isImprovement: improvement > 0,
      isDecline: improvement < 0
    };
  };

  const trend = getImprovementTrend();
  const isPersonalBest = currentPercentage >= personalBest.percentage;

  const getScoreColor = (percentage) => {
    if (percentage >= 90) return 'text-success';
    if (percentage >= 80) return 'text-primary';
    if (percentage >= 70) return 'text-warning';
    return 'text-error';
  };

  return (
    <div className="bg-surface rounded-lg border border-border p-6 mb-6">
      <h3 className="text-xl font-heading font-semibold text-text-primary mb-6">
        Performance Comparison
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Current Performance */}
        <div className="text-center p-4 bg-background rounded-lg">
          <div className="flex items-center justify-center mb-3">
            <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
              <Icon name="Target" size={24} className="text-primary" />
            </div>
          </div>
          <div className={`text-3xl font-bold ${getScoreColor(currentPercentage)} mb-1`}>
            {currentPercentage}%
          </div>
          <div className="text-sm text-text-secondary">Current Score</div>
          {isPersonalBest && (
            <div className="mt-2 inline-flex items-center space-x-1 px-2 py-1 bg-success/10 text-success rounded-full text-xs">
              <Icon name="Crown" size={12} />
              <span>Personal Best!</span>
            </div>
          )}
        </div>

        {/* Improvement Trend */}
        {trend && (
          <div className="text-center p-4 bg-background rounded-lg">
            <div className="flex items-center justify-center mb-3">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                trend.isImprovement ? 'bg-success/10' : trend.isDecline ? 'bg-error/10' : 'bg-border-light'
              }`}>
                <Icon 
                  name={trend.isImprovement ? 'TrendingUp' : trend.isDecline ? 'TrendingDown' : 'Minus'} 
                  size={24} 
                  className={
                    trend.isImprovement ? 'text-success' : trend.isDecline ? 'text-error' : 'text-text-secondary'
                  }
                />
              </div>
            </div>
            <div className={`text-3xl font-bold mb-1 ${
              trend.isImprovement ? 'text-success' : trend.isDecline ? 'text-error' : 'text-text-secondary'
            }`}>
              {trend.value > 0 ? '+' : ''}{trend.value}%
            </div>
            <div className="text-sm text-text-secondary">vs Last Attempt</div>
          </div>
        )}

        {/* Personal Best */}
        <div className="text-center p-4 bg-background rounded-lg">
          <div className="flex items-center justify-center mb-3">
            <div className="w-12 h-12 bg-warning/10 rounded-full flex items-center justify-center">
              <Icon name="Award" size={24} className="text-warning" />
            </div>
          </div>
          <div className={`text-3xl font-bold ${getScoreColor(personalBest.percentage)} mb-1`}>
            {personalBest.percentage}%
          </div>
          <div className="text-sm text-text-secondary">Personal Best</div>
          <div className="text-xs text-text-secondary mt-1">
            {new Date(personalBest.date).toLocaleDateString()}
          </div>
        </div>
      </div>

      {/* Attempt History */}
      {previousAttempts.length > 0 && (
        <div className="mt-6">
          <h4 className="font-medium text-text-primary mb-4 flex items-center space-x-2">
            <Icon name="History" size={16} />
            <span>Recent Attempts</span>
          </h4>
          
          <div className="space-y-3">
            {previousAttempts.slice(-5).reverse().map((attempt, index) => {
              const percentage = Math.round((attempt.score / attempt.totalQuestions) * 100);
              const attemptDate = new Date(attempt.date);
              
              return (
                <div key={index} className="flex items-center justify-between p-3 bg-background rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="text-sm text-text-secondary">
                      {attemptDate.toLocaleDateString()} at {attemptDate.toLocaleTimeString([], { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <div className={`font-semibold ${getScoreColor(percentage)}`}>
                        {percentage}%
                      </div>
                      <div className="text-xs text-text-secondary">
                        {attempt.score}/{attempt.totalQuestions}
                      </div>
                    </div>
                    
                    <div className="w-16 h-2 bg-border rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all duration-300 ${
                          percentage >= 90 ? 'bg-success' : 
                          percentage >= 80 ? 'bg-primary' : 
                          percentage >= 70 ? 'bg-warning' : 'bg-error'
                        }`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Performance Insights */}
      <div className="mt-6 p-4 bg-primary/5 border border-primary/20 rounded-lg">
        <div className="flex items-start space-x-2">
          <Icon name="Lightbulb" size={16} className="text-primary mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-medium text-text-primary mb-2">Performance Insights</h4>
            <div className="space-y-1 text-sm text-text-secondary">
              {isPersonalBest && (
                <p>🎉 Congratulations! You've achieved a new personal best score!</p>
              )}
              {trend && trend.isImprovement && (
                <p>📈 Great improvement! You're {trend.value}% better than your last attempt.</p>
              )}
              {trend && trend.isDecline && (
                <p>📉 Your score decreased by {Math.abs(trend.value)}%. Consider reviewing the material again.</p>
              )}
              {currentPercentage >= 90 && (
                <p>⭐ Excellent performance! You've mastered this topic.</p>
              )}
              {currentPercentage >= 70 && currentPercentage < 90 && (
                <p>👍 Good job! A few more practice sessions could help you reach excellence.</p>
              )}
              {currentPercentage < 70 && (
                <p>💪 Keep practicing! Review the material and try again to improve your understanding.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceComparison;